(ns measure.report-builder.hooks
  (:require ["src/svg/wpp/charts/graph.svg$default" :as Graph]
            ["src/svg/wpp/charts/ranking.svg$default" :as Ranking]
            ["src/svg/wpp/content-actions/color.svg$default" :as Color]
            ["src/svg/wpp/content-actions/data-transfer.svg$default" :as DataTransfer]
            ["src/svg/wpp/content-actions/translate.svg$default" :as Translate]
            ["src/svg/wpp/content/action.svg$default" :as Action]
            ["src/svg/wpp/content/calendar.svg$default" :as Calendar]
            ["src/svg/wpp/content/cart.svg$default" :as Cart]
            ["src/svg/wpp/content/cloud.svg$default" :as Cloud]
            ["src/svg/wpp/content/hub.svg$default" :as Hub]
            ["src/svg/wpp/content/live.svg$default" :as Live]
            ["src/svg/wpp/content/location.svg$default" :as Location]
            ["src/svg/wpp/content/map.svg$default" :as Map]
            ["src/svg/wpp/content/movie.svg$default" :as Movie]
            ["src/svg/wpp/content/people.svg$default" :as People]
            ["src/svg/wpp/content/smartphone.svg$default" :as Smartphone]
            ["src/svg/wpp/files/folder.svg$default" :as Folder]
            [clojure.set :as set]
            [common.queries :refer [use-query]]
            [common.resource.query.adset :as adset.q]
            [common.resource.query.campaign :as campaign.q]
            [common.resource.query.user :as user.q]
            [common.util :as util]
            [common.widgets.daterange-picker :refer [shortcuts-items]]
            [manage.adset.util :as adset-util]
            [uix.core :refer [$ defhook]]
            [uix.re-frame :refer [use-subscribe]]))

(def allowed-channels
  "Reporting support is limited to display, ctv and video channel adsets"
  #{"display" "video" "ctv"})

(def channel-options
  (->> adset-util/channel-options
       (filter #(contains? allowed-channels (:value %)))))

(defhook use-adset-options [selected-channel]
  (let [{:keys [advertiser-id]} (use-subscribe [:url-context])
        {adsets :data} (use-query (adset.q/adsets-query {:advertiser-id advertiser-id}))
        {campaigns :data} (use-query (campaign.q/campaigns-query {:advertiser-id advertiser-id}))

        campaigns* (mapv (fn [{:keys [name id]}]
                           {:campaign-name name
                            :campaign-id id}) campaigns)
        adsets-with-campaign-name (set/project
                                   (set/join adsets campaigns* {:campaignId :campaign-id})
                                   [:id :archived :name :channel :campaign-name :campaign-id])]
    (->> adsets-with-campaign-name
         (remove :archived)
         (keep (fn [{:keys [id name channel campaign-name campaign-id]}]
                 (when (= selected-channel channel)
                   {:label (str name " (" id ")")
                    :value id
                    :campaign (str campaign-name " (" campaign-id ")")
                    :campaignId campaign-id})))
         (sort-by :campaign))))

(defhook use-email-options []
  (let [{:keys [agency-id]} (use-subscribe [:url-context])
        {users :data} (use-query (user.q/users-query {:agency-id agency-id}))]
    (map (fn [{:keys [email name]}]
           {:label name
            :value email})
         users)))

(def display-impression-metrics
  [{:id "test_impression"
    :label "Test Impressions"
    :tooltip "Impressions from test/staging domains"}
   {:id "non_viewable_impression"
    :label "Non-viewable Impressions"
    :tooltip "Impressions that didn't meet IAB viewability (50% pixels, 1 second)"}
   {:id "rendered_impression"
    :label "Rendered Impressions"
    :tooltip "Impressions where the ad was rendered in the browser"}
   {:id "valid_impression"
    :label "Valid Impressions"
    :tooltip "Impressions that passed all validity checks"}])

(def display-click-metrics
  [{:id "test_click"
    :label "Test Clicks"
    :tooltip "Clicks from test/staging domains"}
   {:id "non_viewable_click"
    :label "Non-viewable Clicks"
    :tooltip "Clicks on non-viewable impressions"}])

(def video-metrics
  [{:id "video-progress"
    :label "Video progress"
    :icon ($ Ranking)
    :children [{:id "video_start"
                :label "Video Start"
                :tooltip "Video playback started"}
               {:id "video_first_quartile"
                :label "First Quartile"
                :tooltip "Video reached 25% completion"}
               {:id "video_midpoint"
                :label "Midpoint"
                :tooltip "Video reached 50% completion"}
               {:id "video_third_quartile"
                :label "Third Quartile"
                :tooltip "Video reached 75% completion"}
               {:id "video_complete"
                :label "Video Complete"
                :tooltip "Video played to 100% completion"}]}
   {:id "video-interactions"
    :label "Video interactions"
    :icon ($ Movie)
    :children [{:id "video_mute"
                :label "Mute"
                :tooltip "User muted the video"}
               {:id "video_unmute"
                :label "Unmute"
                :tooltip "User unmuted the video"}
               {:id "video_pause"
                :label "Pause"
                :tooltip "User paused the video"}
               {:id "video_resume"
                :label "Resume"
                :tooltip "User resumed playback after pausing"}
               {:id "video_fullscreen"
                :label "Fullscreen"
                :tooltip "User entered fullscreen mode"}
               {:id "video_exit_fullscreen"
                :label "Exit Fullscreen"
                :tooltip "User exited fullscreen mode"}
               {:id "video_skip"
                :label "Skip"
                :tooltip "User skipped the video ad"}
               {:id "video_accept_invitation"
                :label "Accept Invitation"
                :tooltip "User accepted an invitation to interact"}
               {:id "video_close"
                :label "Close"
                :tooltip "User closed the video ad"}]}])

(def request-metrics
  {:id "requests"
   :label "Requests"
   :icon ($ DataTransfer)
   :children [{:id "request"
               :label "Request"
               :tooltip "Ad request to serve a dynamic ad"}
              {:id "invalid_request"
               :label "Invalid Request"
               :tooltip "Ad request to serve a dynamic ad, flagged as bot/fraudulent traffic"}]})

(defhook use-metric-options [selected-channel]
  (let [standard-metrics [(cond-> {:id "_impressions"
                                   :label "Impressions"
                                   :icon ($ Graph)
                                   :children [{:id "impression"
                                               :label "Impressions"
                                               :tooltip "Valid, non-test, viewable impressions"}
                                              {:id "raw_impression"
                                               :label "Raw Impressions"
                                               :tooltip "All impressions regardless of filtering"}
                                              {:id "invalid_impression"
                                               :label "Invalid Impressions"
                                               :tooltip "Bot/fraudulent traffic"}]}
                            (= selected-channel "display")
                            (update :children concat display-impression-metrics))
                          (cond-> {:id "_clicks"
                                   :label "Clicks"
                                   :icon ($ Action)
                                   :children [{:id "click"
                                               :label "Clicks"
                                               :tooltip "Valid, non-test, viewable clicks"}
                                              {:id "invalid_click"
                                               :label "Invalid Clicks"
                                               :tooltip "Bot/fraudulent clicks"}]}
                            (= selected-channel "display")
                            (update :children concat display-click-metrics))
                          request-metrics]]
    (case selected-channel
      "display" standard-metrics
      ("ctv" "video") (concat standard-metrics video-metrics)
      [])))

(def column-options-general
  {:id "general"
   :label "General"
   :icon ($ Folder)
   :children [{:id "agency_id"
               :label "Agency ID"
               :tooltip "ID of the agency"}
              {:id "adset_id"
               :label "Adset ID"
               :tooltip "ID of the ad set"}
              {:id "adset_name"
               :label "Adset Name"
               :tooltip "Human-readable name of the ad set"}
              {:id "advertiser_id"
               :label "Advertiser ID"
               :tooltip "ID of the advertiser"}
              {:id "advertiser_name"
               :label "Advertiser name"
               :tooltip "Human-readable name of the advertiser"}
              {:id "campaign_id"
               :label "Campaign ID"
               :tooltip "ID of the campaign"}
              {:id "campaign_name"
               :label "Campaign name"
               :tooltip "Human-readable name of the campaign"}
              {:id "workflow"
               :label "Workflow"
               :tooltip "The workflow used to activate the strategy"}]})

(def column-options-creative
  {:id "creative"
   :label "Creative"
   :icon ($ Color)
   :children [{:id "content"
               :label "Content (json)"
               :tooltip "The full JSON blob of the content including all dynamic data"}
              {:id "content_id"
               :label "Content ID"
               :tooltip "The identifier of the content used to group multiple ads having the same message"}
              {:id "variant_id"
               :label "Variant ID"
               :tooltip "The internal identifier of the ad variant"}
              {:id "width"
               :label "Creative Width"
               :tooltip "The width of the creative in pixels"}
              {:id "height"
               :label "Creative Height"
               :tooltip "The height of the creative in pixels"}
              {:id "creative_size"
               :label "Creative Size"
               :tooltip "The width x height of the creative in pixel"}
              {:id "ad_variant_uuid"
               :label "Ad variant UUID"
               :tooltip "The unique identifier of the ad variant served"}
              {:id "ad_variant_revision_uuid"
               :label "Ad variant revision UUID"
               :tooltip "The unique identifier of the specific revision of the ad variant served"}
              {:id "ad_variant_revision"
               :label "Ad variant version number"
               :tooltip "The version number of the ad variant"}
              {:id "creative_preview_url"
               :label "Ad variant link"
               :tooltip "A link to the latest versions of the ad variant in the application"}]})

(def column-options-creative-strategy
  {:id "creative-strategy"
   :label "Creative Strategy"
   :icon ($ Map)
   :children [{:id "decision_tree_id"
               :label "Decision Tree id"
               :tooltip "The identifier of the decision tree used for targetting"}
              {:id "decision_tree_version"
               :label "Decision Tree version"
               :tooltip "The version of the decision tree used for targetting"}
              {:id "decision_tree_path_id"
               :label "Decision Tree Path (ids)"
               :tooltip "The path through the decision tree showing the targeting nodes from root to leaf"}
              {:id "decision_tree_path"
               :label "Decision Tree Path (names)"
               :tooltip "Human-readable path through the decision tree showing targeting nodes from root to leaf"}]})

(defn column-options-ad-serving [selected-channel]
  {:id "ad-serving"
   :label "Ad Serving"
   :icon ($ Hub)
   :children (->> [{:id "iaas_region"
                    :label "Ad Server region"
                    :tooltip "The region where the ad was served from"}
                   (when (= selected-channel "display")
                     {:id "environment"
                      :label "Ad environment"
                      :tooltip "The environment in which this ad was served (display only)"})
                   {:id "invalid_traffic"
                    :label "Invalid Traffic"
                    :tooltip "Boolean to flag if the ad was considered as invalid traffic"}
                   {:id "test_domain"
                    :label "Test domain"
                    :tooltip "Boolean to flag if the ad was considered coming from a test domain/environment"}
                   {:id "referer"
                    :label "Referrer"
                    :tooltip "The referrer url where the ad was served from"}]
                  (keep identity)
                  vec)})

(def column-options-date-time
  {:id "date-time"
   :label "Date Time"
   :icon ($ Calendar)
   :children [{:id "event_date"
               :label "Date"
               :tooltip "The date when the ad was served"}
              {:id "event_month"
               :label "Month"
               :tooltip "The month when the ad was served"}
              {:id "client_day_of_week"
               :label "Day of week (ad consumer)"
               :tooltip "The day of the week viewed from the ad consumer's perspective"}
              {:id "client_day_of_month"
               :label "Day of Month (ad consumer)"
               :tooltip "The day of the month viewed from the ad consumer's perspective"}
              {:id "client_timezone"
               :label "Timezone (ad consumer)"
               :tooltip "The timezone in which the ad consumer was located"}]})

(def column-options-weather
  {:id "weather"
   :label "Weather"
   :icon ($ Cloud)
   :children [{:id "weather_temperature_of_today"
               :label "Temperature of Today"
               :tooltip "Today's temperature in degrees Celsius"}
              {:id "weather_temperature_of_tomorrow"
               :label "Temperature of Tomorrow"
               :tooltip "Tomorrow's temperature in degrees Celsius"}
              {:id "weather_weather_of_today"
               :label "Today's Weather Condition"
               :tooltip "Today's weather condition (Clear, Cloudy, Rain, Snow, Storm, etc.)"}
              {:id "weather_weather_of_tomorrow"
               :label "Tomorrow's Weather Condition"
               :tooltip "Tomorrow's weather condition (Clear, Cloudy, Rain, Snow, Storm, etc.)"}
              {:id "weather_windspeed_of_today"
               :label "Today's Wind Speed"
               :tooltip "Today's wind speed in meters per second"}]})

(def column-options-geolocation
  {:id "geolocation"
   :label "Geolocation"
   :icon ($ Location)
   :children [{:id "location_city"
               :label "City"
               :tooltip "City name from geo-location"}
              {:id "location_country_code"
               :label "Country Code"
               :tooltip "ISO country code from geo-location"}
              {:id "location_postal_code"
               :label "Postal Code"
               :tooltip "Postal/ZIP code from geo-location"}
              {:id "location_region"
               :label "Region"
               :tooltip "Geographic region/state/province"}
              {:id "location_source"
               :label "Geo source"
               :tooltip "The source that was used to determine the location"}]})

(def column-options-device
  {:id "device"
   :label "Device"
   :icon ($ Smartphone)
   :children [{:id "device_os"
               :label "Operating System"
               :tooltip "Operating system (Mac OS, iOS, Android, Windows)"}
              {:id "device_browser"
               :label "Browser"
               :tooltip "Browser family"}
              {:id "device_is_app"
               :label "Is App"
               :tooltip "Boolean indicating if delivered in mobile app environment"}
              {:id "device_is_touch"
               :label "Is Touch"
               :tooltip "Boolean indicating if device has touch support"}]})

(def column-options-language
  {:id "_language"
   :label "Language"
   :icon ($ Translate)
   :children [{:id "language"
               :label "Language"
               :tooltip "Preferred language"}]})

(def column-options-dsp-signals
  {:id "dsp-signals"
   :label "DSP Signals"
   :icon ($ People)
   :children [{:id "dsp_line_id"
               :label "DSP Line ID"
               :tooltip "DSP platform line item identifier from macro"}
              {:id "dsp_advertiser_id"
               :label "DSP Advertiser ID"
               :tooltip "DSP platform advertiser identifier from macro"}
              {:id "dsp_creative_id"
               :label "DSP Creative ID"
               :tooltip "DSP platform creative identifier from macro"}
              {:id "dsp_deal_id"
               :label "DSP Deal ID"
               :tooltip "DSP platform deal identifier from macro"}
              {:id "dsp_io_id"
               :label "DSP IO ID"
               :tooltip "DSP platform insertion order identifier from macro"}
              {:id "dsp_placement_id"
               :label "DSP Placement ID"
               :tooltip "DSP platform placement identifier from macro"}
              {:id "dsp_publisher_id"
               :label "DSP Publisher ID"
               :tooltip "DSP platform publisher identifier from macro"}
              {:id "dsp_site_url"
               :label "DSP Site URL"
               :tooltip "DSP platform site URL from macro"}
              {:id "dsp_split"
               :label "DSP Split"
               :tooltip "DSP platform split test identifier from macro"}
              {:id "dsp_split_code"
               :label "DSP Split Code"
               :tooltip "DSP platform split test code from macro"}]})

(def column-options-cm360-signal
  {:id "cm360-signal"
   :label "CM360 Signal"
   :icon ($ Live)
   :children [{:id "cm360_advertiser_id"
               :label "CM360 Advertiser ID"
               :tooltip "Campaign Manager 360 advertiser identifier"}
              {:id "cm360_ad_id"
               :label "CM360 Ad ID"
               :tooltip "Campaign Manager 360 ad identifier"}
              {:id "cm360_campaign_id"
               :label "CM360 Campaign ID"
               :tooltip "Campaign Manager 360 campaign identifier"}
              {:id "cm360_creative_id"
               :label "CM360 Creative ID"
               :tooltip "Campaign Manager 360 creative identifier"}
              {:id "cm360_placement_id"
               :label "CM360 Placement ID"
               :tooltip "Campaign Manager 360 placement identifier"}
              {:id "cm360_site_id"
               :label "CM360 Site ID"
               :tooltip "Campaign Manager 360 site identifier"}]})

(def column-options-products
  {:id "products"
   :label "Products"
   :icon ($ Cart)
   :children [{:id "sku_1"
               :label "SKU #1"
               :tooltip "The 1st product SKU associated with the impression"}
              {:id "dynamic_product_analytics_1"
               :label "Details #1"
               :tooltip "Extended product analytics and metadata for the 1st SKU"}
              {:id "sku_2"
               :label "SKU #2"
               :tooltip "The 2nd product SKU associated with the impression"}
              {:id "dynamic_product_analytics_2"
               :label "Details #2"
               :tooltip "Extended product analytics and metadata for the 2nd SKU"}
              {:id "sku_3"
               :label "SKU #3"
               :tooltip "The 3rd product SKU associated with the impression"}
              {:id "dynamic_product_analytics_3"
               :label "Details #3"
               :tooltip "Extended product analytics and metadata for the 3rd SKU"}
              {:id "sku_4"
               :label "SKU #4"
               :tooltip "The 4th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_4"
               :label "Details #4"
               :tooltip "Extended product analytics and metadata for the 4th SKU"}
              {:id "sku_5"
               :label "SKU #5"
               :tooltip "The 5th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_5"
               :label "Details #5"
               :tooltip "Extended product analytics and metadata for the 5th SKU"}
              {:id "sku_6"
               :label "SKU #6"
               :tooltip "The 6th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_6"
               :label "Details #6"
               :tooltip "Extended product analytics and metadata for the 6th SKU"}
              {:id "sku_7"
               :label "SKU #7"
               :tooltip "The 7th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_7"
               :label "Details #7"
               :tooltip "Extended product analytics and metadata for the 7th SKU"}
              {:id "sku_8"
               :label "SKU #8"
               :tooltip "The 8th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_8"
               :label "Details #8"
               :tooltip "Extended product analytics and metadata for the 8th SKU"}
              {:id "sku_9"
               :label "SKU #9"
               :tooltip "The 9th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_9"
               :label "Details #9"
               :tooltip "Extended product analytics and metadata for the 9th SKU"}
              {:id "sku_10"
               :label "SKU #10"
               :tooltip "The 10th product SKU associated with the impression"}
              {:id "dynamic_product_analytics_10"
               :label "Details #10"
               :tooltip "Extended product analytics and metadata for the 10th SKU"}]})

(defhook use-column-options [selected-channel]
  [column-options-general
   column-options-creative
   column-options-creative-strategy
   (column-options-ad-serving selected-channel)
   column-options-date-time
   column-options-weather
   column-options-geolocation
   column-options-device
   column-options-language
   column-options-dsp-signals
   column-options-cm360-signal
   column-options-products])

(def timezone-options
  (map #(hash-map :label % :value %) util/timezones))

(def format-options
  [{:label "Excel"
    :value "xlsx"}
   {:label "CSV"
    :value "csv"}])

(def schedule-options
  [{:label "No schedule"
    :value nil}
   {:label "Daily"
    :value "daily"}
   {:label "Weekly"
    :value "weekly"}
   {:label "Monthly"
    :value "monthly"}])

(def date-phrase-options
  (->> (shortcuts-items)
       (remove (comp #{"year_to_today"} :id))
       (mapv (fn [{:keys [id label]}]
               {:label label :value id}))))
